/**
 * space/guestbook.js — the visitor guestbook (GDD 9.4).
 *
 * A lectern by the entrance holding an open book. Visitors leave one of three
 * reactions; the twenty most recent are kept and shown newest first. The write
 * path is deliberately tiny and public, because the networking agent is the
 * real caller — a remote visitor's reaction arrives as `add(reaction, name)`
 * and needs no knowledge of how the book is drawn.
 *
 * @module space/guestbook
 */

import * as THREE from 'three';
import { GUESTBOOK_REACTIONS, SPACE } from '../constants.js';
import { pointer, menuHost } from './pointer.js';
import { createPanel } from '../ui/panels.js';

/** @param {string} id @returns {object|undefined} */
export function reactionSpec(id) {
  return GUESTBOOK_REACTIONS.find((r) => r.id === id);
}

/**
 * The lectern, the open book, and the list of reactions on it.
 */
export class Guestbook {
  /**
   * @param {{ room: import('./space-world.js').SpaceRoom, readOnly?: boolean,
   *           onSign?: (reactionId: string) => void }} opts
   */
  constructor(opts) {
    this.room = opts.room;
    this.readOnly = !!opts.readOnly;
    this.onSign = opts.onSign;

    this.group = new THREE.Group();
    this.group.name = 'space-guestbook';
    this.room.root.add(this.group);

    /** @type {Array<{reaction: string, emoji: string, from: string, day: number}>} */
    this.entries = [];
    this._panel = null;
    this._idPrefix = `${this.room.name}-book`;
  }

  /**
   * @param {{ guestbook: Array<object> }} model
   */
  rebuild(model) {
    this.dispose();
    this.model = model;
    this.entries = (model.guestbook || []).slice(-SPACE.guestbookMax);

    const anchor = this.room.anchors.guestbook;
    this.group.position.copy(anchor.position);
    this.group.rotation.y = anchor.rotation.y;

    this._buildLectern();
    this._buildPanel();
    this.refresh();
  }

  /** @private */
  _buildLectern() {
    const wood = new THREE.MeshStandardMaterial({ color: 0x6b4a2a, roughness: 0.9 });

    const column = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.15, 1.0, 10), wood);
    column.position.y = 0.5;
    column.castShadow = true;
    this.group.add(column);

    const base = new THREE.Mesh(new THREE.CylinderGeometry(0.26, 0.3, 0.07, 14), wood);
    base.position.y = 0.035;
    this.group.add(base);

    const desk = new THREE.Mesh(new THREE.BoxGeometry(0.56, 0.05, 0.4), wood);
    desk.position.y = 1.02;
    desk.rotation.x = -0.32;
    desk.castShadow = true;
    this.group.add(desk);

    // The open book: two pages tilted into a shallow V.
    const paper = new THREE.MeshStandardMaterial({ color: 0xf6f1e2, roughness: 0.95 });
    for (const sx of [-1, 1]) {
      const page = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.014, 0.34), paper);
      page.position.set(sx * 0.125, 1.07, 0.012);
      page.rotation.x = -0.32;
      page.rotation.z = sx * 0.05;
      this.group.add(page);
    }
    const spine = new THREE.Mesh(
      new THREE.BoxGeometry(0.03, 0.02, 0.34),
      new THREE.MeshStandardMaterial({ color: 0xa33b3b, roughness: 0.8 }),
    );
    spine.position.set(0, 1.075, 0.012);
    spine.rotation.x = -0.32;
    this.group.add(spine);
    this._book = spine;

    if (this.readOnly) return;
    pointer.register(desk, {
      id: `${this._idPrefix}:sign`,
      group: 'guestbook',
      label: 'Sign the guestbook',
      onSelect: () => this.openReactions(),
    });
  }

  /** @private */
  _buildPanel() {
    this._panel = createPanel({
      title: 'GUESTBOOK', width: 0.78, height: 0.72, align: 'left', lines: [],
    });
    this._panel.position.set(0, 1.72, -0.16);
    this.group.add(this._panel);
  }

  /** Redraw the panel from `entries`. */
  refresh() {
    if (!this._panel) return;
    if (this.entries.length === 0) {
      this._panel.setLines(['No visitors yet.', '', 'Sign it yourself —', 'someone has to be first.']);
      return;
    }
    const recent = this.entries.slice(-5).reverse();
    this._panel.setLines(recent.map((e) => `${e.emoji} ${e.from}  · day ${e.day}`));
  }

  /** Open the three-reaction picker. */
  openReactions() {
    menuHost.open(
      {
        key: 'guestbook',
        parent: this.group,
        position: new THREE.Vector3(0.62, 1.5, 0),
        rotationY: 0.5,
      },
      {
        title: 'LEAVE A REACTION',
        idPrefix: `${this._idPrefix}-react`,
        items: GUESTBOOK_REACTIONS.map((r) => ({ id: r.id, label: `${r.emoji}  ${r.label}` })),
        onPick: (id) => { menuHost.close(); this.onSign?.(id); },
      },
    );
  }

  /**
   * Record a reaction. The trimming to twenty happens here so every caller —
   * local player, remote visitor, test — gets the same bound.
   * @param {string} reactionId
   * @param {string} fromName
   * @param {number} day
   * @returns {object|null} The stored entry.
   */
  add(reactionId, fromName, day) {
    const spec = reactionSpec(reactionId);
    if (!spec) return null;
    const entry = {
      reaction: spec.id,
      emoji: spec.emoji,
      from: String(fromName || 'A goose').slice(0, 24),
      day,
    };
    this.entries.push(entry);
    while (this.entries.length > SPACE.guestbookMax) this.entries.shift();
    this.refresh();
    return entry;
  }

  /** @returns {Array<object>} Newest last, as stored. */
  list() {
    return this.entries.map((e) => ({ ...e }));
  }

  update(dt) {
    // The book bobs a hair so the lectern is not visually inert.
    if (this._book) this._book.position.y = 1.075 + Math.sin(Date.now() * 0.0012) * 0.002;
  }

  dispose() {
    pointer.unregister(`${this._idPrefix}:sign`);
    this._panel?.dispose?.();
    this._panel = null;
    this.entries.length = 0;
    while (this.group.children.length) this.group.remove(this.group.children[0]);
  }
}
